import { useMemo } from 'react'

import { useFlag } from '@hooks/useFlag'
import { useRouter } from 'next/router'

const CONTROL = 'C_BASELINE'
const TREATMENT = 'T1_ALL_UPDATES'
const EXPERIMENT_UNIQUE_IDENTIFIER = '__none__'
const PENDING_STATE = '__pending__'

/** TODO: update name schema check to be N-numbered once variants come online */
const getVariantId = (x: string) => (x.split('_')[0] ?? '') + '_'

const TREATMENTS = ['T1_', 'T2_', 'T3_', 'T4_'] as const
type Treatment = typeof TREATMENT
const isTreatmentVariant = (x: unknown): x is Treatment => {
  return typeof x === 'string' && TREATMENTS.includes(getVariantId(x) as never)
}

const VARIANTS = ['C_', ...TREATMENTS] as const
type Variant = typeof VARIANTS[number]
const isVariant = (x: unknown): x is Variant => {
  return typeof x === 'string' && VARIANTS.includes(getVariantId(x) as never)
}

export const useAuthExperiment = () => {
  const group = useFlag('auth')

  const experiment = useMemo(() => {
    const config = group ? { auth: group } : undefined

    const uid = EXPERIMENT_UNIQUE_IDENTIFIER
    const status = !config ? 'pending' : 'ready'
    const enabled = [
      undefined,
      status === 'pending'
        ? ({ status } as const)
        : ({ uid, status, value: group.enabled } as const),
    ] as const

    const variant = group ? (group.enabled ? TREATMENT : CONTROL) : undefined

    const result = [
      undefined,
      status === 'pending'
        ? ({ status } as const)
        : ({
            uid,
            status,
            value: variant,
          } as const),
    ] as const

    const pages = {
      home: result,
      you: result,
      eligibility: result,
      auth: result,
    } as const

    const getPageVariant = (route: string) => {
      const { value } = getVariant(route, pages)
      const isTreatment = isTreatmentVariant(value)
      const isControl = !isTreatment && isVariant(value)
      const isReady = isControl || isTreatment

      if (!isReady) {
        return { isReady: false } as const
      }

      return {
        isReady: true,
        isControl,
        isTreatment,
        variant,
      } as const
    }

    const getTreatment = (route: string): Treatment | '' => {
      const { isReady, variant } = getPageVariant(route)
      return isReady && isTreatmentVariant(variant) ? variant : ''
    }

    return {
      /** kill switch */
      enabled,

      /**
       * control vs. treatments
       * per-page
       */
      pages,

      /** use to conditionally apply auth updates within the flow */
      getTreatment,
    }
  }, [group])

  return experiment
}

export const useAuthTreatment = () => {
  const router = useRouter()
  const { getTreatment } = useAuthExperiment()
  return useMemo(() => getTreatment(router.route), [getTreatment, router.route])
}

type PageResults = ReturnType<typeof useAuthExperiment>['pages']
const SUPPORTED_PAGES_MAP: Record<string, keyof PageResults> = {
  '/': 'home',
  '/enroll/you': 'you',
  '/auth': 'auth',
  '/renewal/eligibility/eligibility': 'eligibility',
}

export function getVariant(route: string, pages: PageResults) {
  const key = SUPPORTED_PAGES_MAP[route]
  if (!key) {
    return { value: CONTROL, uid: EXPERIMENT_UNIQUE_IDENTIFIER } as const
  }

  const [, result] = pages[key]
  if (result?.status !== 'ready') {
    return { value: PENDING_STATE, uid: PENDING_STATE } as const
  }

  const { uid, value } = result
  return {
    value,
    uid,
  } as const
}
